// Simple color palette utility with contrast-aware ordering
// Palettes are defined in CSS variables under src/sass (see _colors.scss) and read at runtime.

export type PaletteName = 'tango' | 'adwaita';
type PaletteId = PaletteName | string;

const PALETTE_CSS_VARS: Record<PaletteName, string> = {
  tango: '--palette-tango-groups',
  adwaita: '--palette-adwaita-groups'
};

// Read palette groups from CSS variables. Format per variable:
//   group1Color1,group1Color2|group2Color1,group2Color2,...
// Groups preserve intended contrast ordering; we interleave colors by index.
function readPaletteGroups(palette: PaletteId): string[][] {
  const cssVar = PALETTE_CSS_VARS[palette as PaletteName];
  if (!cssVar || typeof document === 'undefined') return [];
  const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  if (!raw) return [];
  return raw
    .split('|')
    .map(group => group.split(',').map(c => c.trim()).filter(Boolean))
    .filter(group => group.length > 0);
}

// Interleave chunks by index to separate similar hues.
// Example: for 3-sized chunks, output order: c[0] of each chunk, then c[1] of each, then c[2] of each...
function interleaveChunks(chunks: string[][], count?: number): string[] {
  if (!chunks.length) return [];
  const maxLen = Math.max(...chunks.map(c => c.length));
  const out: string[] = [];
  for (let i = 0; i < maxLen; i++) {
    for (const chunk of chunks) {
      const color = chunk[i];
      if (color) out.push(color);
      if (count && out.length >= count) return out.slice(0, count);
    }
  }
  return count ? out.slice(0, count) : out;
}

export const ColorPalettes = {
  getDomain(palette: PaletteId, count?: number): string[] {
    const groups = readPaletteGroups(palette);
    return interleaveChunks(groups, count);
  },
  tango(): string[] {
    return readPaletteGroups('tango').flat();
  },
  adwaita(): string[] {
    return readPaletteGroups('adwaita').flat();
  }
};
